import { Component } from '@theme/component';
import {
  preloadImage,
} from '@theme/utilities';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

const MIN_ZOOM = 1;
const MAX_ZOOM = 6;
const ZOOM_STEP = 0.5;
const WHEEL_ZOOM_SPEED = 0.003;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
const DRAG_THRESHOLD = 5;

/**
 * Modern Lightbox Zoom Dialog
 * Shows a single image at a time in a dark overlay with zoom, pan, and navigation.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements (source gallery).
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());
  #activeIndex = 0;
  #scale = MIN_ZOOM;
  #translate = { x: 0, y: 0 };
  #isDragging = false;
  #startPosition = { x: 0, y: 0 };
  #startTranslate = { x: 0, y: 0 };
  #hasDraggedBeyondThreshold = false;
  #lastTapTime = 0;
  #lastTapPosition = null;
  #initialPinchDistance = 0;
  #startScale = MIN_ZOOM;
  #animationFrame = null;
  #controller = new AbortController();
  /** @type {Map<number, string>} Pre-loaded image sources from the main gallery */
  #cachedImageSources = new Map();

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
    }
  }

  /**
   * Opens the lightbox at the given index.
   * @param {number} index
   * @param {PointerEvent} event
   */
  async open(index, event) {
    event.preventDefault();
    this.#activeIndex = index;
    this.#resetZoomState();

    // Cache already-loaded images from the main gallery for instant display
    this.#cacheMainGalleryImages();

    const { dialog } = this.refs;

    // Build lightbox content
    this.#buildLightboxContent();

    // Show dialog
    dialog.showModal();
    document.documentElement.setAttribute('scroll-lock', '');

    // Bind events
    this.#bindLightboxEvents();

    // Load high-res image in background
    this.#loadCurrentHighRes();

    // Select thumbnail
    this.#updateThumbnailSelection();

    // Update counter
    this.#updateCounter();
  }

  /**
   * Scans the main gallery (slideshow/grid) for already-loaded images
   * and caches their currentSrc for instant lightbox display.
   */
  #cacheMainGalleryImages() {
    this.#cachedImageSources.clear();
    const gallery = this.closest('media-gallery');
    if (!gallery) return;

    // Get visible gallery images (from slideshow slides or grid)
    const galleryContainers = gallery.querySelectorAll(
      'slideshow-slide.product-media-container, .media-gallery__grid .product-media-container'
    );

    galleryContainers.forEach((container, i) => {
      const img = container.querySelector('img.product-media__image');
      if (img && img.currentSrc) {
        this.#cachedImageSources.set(i, img.currentSrc);
      }
    });
  }

  /**
   * Builds the lightbox HTML inside the dialog
   */
  #buildLightboxContent() {
    const { dialog, media } = this.refs;
    const currentMedia = media[this.#activeIndex];
    if (!currentMedia) return;

    // Find or create lightbox container
    let lightbox = dialog.querySelector('.lightbox');
    if (!lightbox) {
      lightbox = document.createElement('div');
      lightbox.className = 'lightbox';
      lightbox.innerHTML = `
        <div class="lightbox__backdrop"></div>
        <div class="lightbox__image-wrapper">
          <div class="lightbox__image-container"></div>
        </div>
        <div class="lightbox__controls">
          <button type="button" class="lightbox__btn lightbox__btn--zoom-in" aria-label="Zoom in">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              <line x1="11" y1="8" x2="11" y2="14"></line>
              <line x1="8" y1="11" x2="14" y2="11"></line>
            </svg>
          </button>
          <button type="button" class="lightbox__btn lightbox__btn--zoom-out" aria-label="Zoom out">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              <line x1="8" y1="11" x2="14" y2="11"></line>
            </svg>
          </button>
        </div>
        <div class="lightbox__zoom-level">100%</div>
        <button type="button" class="lightbox__btn lightbox__btn--nav lightbox__btn--prev" aria-label="Previous image">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
        </button>
        <button type="button" class="lightbox__btn lightbox__btn--nav lightbox__btn--next" aria-label="Next image">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="9 18 15 12 9 6"></polyline>
          </svg>
        </button>
      `;
      // Insert before thumbnails container
      const thumbContainer = dialog.querySelector('.lightbox-thumbnails-strip');
      if (thumbContainer) {
        dialog.insertBefore(lightbox, thumbContainer);
      } else {
        dialog.appendChild(lightbox);
      }
    }

    // Update the image
    this.#updateLightboxImage();
    this.#updateNavVisibility();
  }

  /**
   * Updates the displayed media in the lightbox (image or video)
   */
  #updateLightboxImage() {
    const { dialog, media } = this.refs;
    const container = dialog.querySelector('.lightbox__image-container');
    if (!container) return;

    const currentMedia = media[this.#activeIndex];
    if (!currentMedia) return;

    // Stop any playing video in the current container
    this.#stopCurrentVideo(container);

    // Clear container
    container.innerHTML = '';

    const mediaType = currentMedia.getAttribute('data-media-type') || currentMedia.dataset.mediaType || '';
    const isVideo = mediaType === 'video' || mediaType === 'external_video';

    if (isVideo) {
      // Handle video media
      this.#renderVideoInLightbox(currentMedia, container);
    } else {
      // Handle image media
      this.#renderImageInLightbox(currentMedia, container);
    }

    // Reset zoom for new media
    this.#resetZoomState();
    this.#applyTransform();

    // Toggle zoom controls visibility (hide for videos)
    const zoomControls = dialog.querySelector('.lightbox__controls');
    if (zoomControls) {
      zoomControls.style.display = isVideo ? 'none' : '';
    }

    // Update cursor for video
    const wrapper = dialog.querySelector('.lightbox__image-wrapper');
    if (wrapper) {
      wrapper.style.cursor = isVideo ? 'default' : 'zoom-in';
    }
  }

  /**
   * Renders an image in the lightbox container
   */
  #renderImageInLightbox(sourceMedia, container) {
    const sourceImg = sourceMedia.querySelector('img.product-media__image');
    if (!sourceImg) return;

    const img = document.createElement('img');
    img.className = 'lightbox__image';
    img.alt = sourceImg.alt || '';
    img.draggable = false;

    // Use cached (already-loaded) gallery image for instant display
    const cachedSrc = this.#cachedImageSources.get(this.#activeIndex);
    const highResUrl = sourceImg.getAttribute('data_max_resolution');

    if (cachedSrc) {
      // Show instantly with the already-downloaded gallery image
      img.src = cachedSrc;
      container.appendChild(img);

      // Upgrade to high-res in the background
      if (highResUrl && highResUrl !== cachedSrc) {
        const highRes = new Image();
        highRes.onload = () => {
          // Only swap if this image is still displayed
          if (img.isConnected) {
            img.src = highResUrl;
            img.srcset = '';
          }
        };
        highRes.src = highResUrl;
      }
    } else if (highResUrl) {
      // No cached source, use high-res directly
      img.src = highResUrl;
      img.srcset = '';
      container.appendChild(img);
    } else {
      // Fallback
      img.src = sourceImg.currentSrc || sourceImg.src;
      container.appendChild(img);
    }
  }

  /**
   * Renders a video in the lightbox container
   */
  #renderVideoInLightbox(sourceMedia, container) {
    const videoWrapper = document.createElement('div');
    videoWrapper.className = 'lightbox__video-wrapper';

    // Strategy: Extract only the raw video/iframe from the deferred-media template
    // and render it directly — bypassing any product-media wrapper CSS
    const deferredMedia = sourceMedia.querySelector('deferred-media');

    if (deferredMedia) {
      // Check for already-rendered video/iframe first
      let videoEl = deferredMedia.querySelector('video');
      let iframeEl = deferredMedia.querySelector('iframe');

      if (!videoEl && !iframeEl) {
        // Load from template
        const template = deferredMedia.querySelector('template');
        if (template && template.content.firstElementChild) {
          const content = template.content.firstElementChild;
          // The content could be a video, iframe, or a wrapper containing one
          if (content.tagName === 'VIDEO') {
            videoEl = content;
          } else if (content.tagName === 'IFRAME') {
            iframeEl = content;
          } else {
            videoEl = content.querySelector('video');
            iframeEl = content.querySelector('iframe');
          }
        }
      }

      if (videoEl) {
        const clone = /** @type {HTMLVideoElement} */ (videoEl.cloneNode(true));
        clone.controls = true;
        clone.muted = false;
        clone.autoplay = true;
        clone.playsInline = true;
        // Remove any forced sizing — let it use natural dimensions
        clone.removeAttribute('style');
        clone.style.maxWidth = '100%';
        clone.style.maxHeight = '80vh';
        clone.style.width = 'auto';
        clone.style.height = 'auto';
        clone.style.objectFit = 'contain';
        clone.style.display = 'block';

        videoWrapper.appendChild(clone);
        container.appendChild(videoWrapper);

        requestAnimationFrame(() => clone.play().catch(() => {}));
      } else if (iframeEl) {
        const clone = /** @type {HTMLIFrameElement} */ (iframeEl.cloneNode(true));
        clone.removeAttribute('style');
        videoWrapper.appendChild(clone);
        container.appendChild(videoWrapper);
      } else {
        // Fallback: show preview image
        this.#renderVideoFallbackImage(sourceMedia, container);
      }
    } else {
      // No deferred-media, try direct video/iframe
      const video = sourceMedia.querySelector('video');
      const iframe = sourceMedia.querySelector('iframe');

      if (video) {
        const clone = /** @type {HTMLVideoElement} */ (video.cloneNode(true));
        clone.controls = true;
        clone.autoplay = true;
        clone.removeAttribute('style');
        clone.style.maxWidth = '100%';
        clone.style.maxHeight = '80vh';
        clone.style.width = 'auto';
        clone.style.height = 'auto';
        clone.style.objectFit = 'contain';
        clone.style.display = 'block';

        videoWrapper.appendChild(clone);
        container.appendChild(videoWrapper);
        requestAnimationFrame(() => clone.play().catch(() => {}));
      } else if (iframe) {
        const clone = /** @type {HTMLIFrameElement} */ (iframe.cloneNode(true));
        clone.removeAttribute('style');
        videoWrapper.appendChild(clone);
        container.appendChild(videoWrapper);
      } else {
        this.#renderVideoFallbackImage(sourceMedia, container);
      }
    }
  }

  /**
   * Renders fallback preview image for a video
   */
  #renderVideoFallbackImage(sourceMedia, container) {
    const sourceImg = sourceMedia.querySelector('img');
    if (sourceImg) {
      const img = document.createElement('img');
      img.className = 'lightbox__image';
      img.src = sourceImg.currentSrc || sourceImg.src;
      img.alt = sourceImg.alt || '';
      container.appendChild(img);
    }
  }

  /**
   * Stops any currently playing video in a container
   */
  #stopCurrentVideo(container) {
    const videos = container.querySelectorAll('video');
    videos.forEach(v => { v.pause(); v.src = ''; });

    const iframes = container.querySelectorAll('iframe');
    iframes.forEach(iframe => {
      // Pause YouTube/Vimeo
      try {
        if (iframe.src.includes('youtube')) {
          iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        } else if (iframe.src.includes('vimeo')) {
          iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
        }
      } catch (e) {}
      iframe.src = '';
    });
  }

  /**
   * Updates prev/next button visibility
   */
  #updateNavVisibility() {
    const { dialog, media } = this.refs;
    const prevBtn = dialog.querySelector('.lightbox__btn--prev');
    const nextBtn = dialog.querySelector('.lightbox__btn--next');

    // Only show nav if there are multiple images
    const imageMedia = media.filter(m => m.classList.contains('product-media-container--image'));
    if (imageMedia.length <= 1) {
      prevBtn?.classList.add('lightbox__btn--hidden');
      nextBtn?.classList.add('lightbox__btn--hidden');
    } else {
      prevBtn?.classList.remove('lightbox__btn--hidden');
      nextBtn?.classList.remove('lightbox__btn--hidden');
    }
  }

  /**
   * Binds lightbox interaction events
   */
  #bindLightboxEvents() {
    // Abort previous bindings
    this.#controller.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const { dialog } = this.refs;
    const options = { passive: false, signal };

    const imageWrapper = dialog.querySelector('.lightbox__image-wrapper');
    if (!imageWrapper) return;

    // Mouse wheel zoom
    imageWrapper.addEventListener('wheel', this.#handleWheel, options);

    // Mouse drag
    imageWrapper.addEventListener('mousedown', this.#handleMouseDown, options);
    window.addEventListener('mousemove', this.#handleMouseMove, options);
    window.addEventListener('mouseup', this.#handleMouseUp, options);

    // Touch events (pinch & pan)
    imageWrapper.addEventListener('touchstart', this.#handleTouchStart, options);
    imageWrapper.addEventListener('touchmove', this.#handleTouchMove, options);
    imageWrapper.addEventListener('touchend', this.#handleTouchEnd, options);

    // Click (double-click/tap to zoom toggle)
    imageWrapper.addEventListener('click', this.#handleClick, options);

    // Navigation buttons
    const prevBtn = dialog.querySelector('.lightbox__btn--prev');
    const nextBtn = dialog.querySelector('.lightbox__btn--next');
    prevBtn?.addEventListener('click', (e) => { e.stopPropagation(); this.#navigate(-1); }, { signal });
    nextBtn?.addEventListener('click', (e) => { e.stopPropagation(); this.#navigate(1); }, { signal });

    // Zoom controls
    const zoomInBtn = dialog.querySelector('.lightbox__btn--zoom-in');
    const zoomOutBtn = dialog.querySelector('.lightbox__btn--zoom-out');
    zoomInBtn?.addEventListener('click', (e) => { e.stopPropagation(); this.#zoomCenter(ZOOM_STEP); }, { signal });
    zoomOutBtn?.addEventListener('click', (e) => { e.stopPropagation(); this.#zoomCenter(-ZOOM_STEP); }, { signal });

    // Keyboard
    dialog.addEventListener('keydown', this.#handleKeyDown, { signal });

    // Backdrop click to close
    const backdrop = dialog.querySelector('.lightbox__backdrop');
    backdrop?.addEventListener('click', () => this.close(), { signal });
  }

  // ─── WHEEL ZOOM ───────────────────

  /** @param {WheelEvent} event */
  #handleWheel = (event) => {
    event.preventDefault();
    const delta = -event.deltaY * WHEEL_ZOOM_SPEED;
    const rect = this.refs.dialog.querySelector('.lightbox__image-wrapper')?.getBoundingClientRect();
    if (!rect) return;

    const clientX = event.clientX;
    const clientY = event.clientY;
    this.#zoomToPoint(
      Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, this.#scale + delta * this.#scale)),
      clientX,
      clientY,
      rect
    );
  };

  // ─── MOUSE DRAG (PAN) ────────────

  /** @param {MouseEvent} event */
  #handleMouseDown = (event) => {
    if (event.button !== 0) return;
    event.preventDefault();
    this.#startDrag(event.clientX, event.clientY);
  };

  /** @param {MouseEvent} event */
  #handleMouseMove = (event) => {
    if (!this.#isDragging) return;
    event.preventDefault();
    this.#processDrag(event.clientX, event.clientY);
  };

  /** @param {MouseEvent} event */
  #handleMouseUp = (event) => {
    if (!this.#isDragging) return;
    this.#isDragging = false;
    this.#hasDraggedBeyondThreshold = false;
  };

  // ─── CLICK (DOUBLE-CLICK ZOOM) ───

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    if (this.#hasDraggedBeyondThreshold) return;

    // Check for double-click/tap
    const now = performance.now();
    if (now - this.#lastTapTime < DOUBLE_TAP_DELAY) {
      event.preventDefault();
      const rect = this.refs.dialog.querySelector('.lightbox__image-wrapper')?.getBoundingClientRect();
      if (!rect) return;

      if (this.#scale > MIN_ZOOM + 0.1) {
        // Reset zoom
        this.#resetZoomState();
        this.#applyTransform();
      } else {
        // Zoom to 2.5x at click point
        this.#zoomToPoint(2.5, event.clientX, event.clientY, rect);
      }
      this.#lastTapTime = 0;
    } else {
      this.#lastTapTime = now;
    }
  };

  // ─── TOUCH (PINCH & PAN) ─────────

  /** @param {TouchEvent} event */
  #handleTouchStart = (event) => {
    event.preventDefault();
    if (event.touches.length === 2) {
      const t1 = event.touches[0];
      const t2 = event.touches[1];
      this.#initialPinchDistance = this.#getDistance(t1, t2);
      this.#startScale = this.#scale;
      this.#isDragging = false;
    } else if (event.touches.length === 1) {
      const touch = event.touches[0];
      const now = performance.now();
      const timeSinceLastTap = now - this.#lastTapTime;

      if (timeSinceLastTap < DOUBLE_TAP_DELAY && this.#lastTapPosition) {
        const dist = this.#getDistance(touch, this.#lastTapPosition);
        if (dist < DOUBLE_TAP_DISTANCE) {
          // Double tap
          const rect = this.refs.dialog.querySelector('.lightbox__image-wrapper')?.getBoundingClientRect();
          if (rect) {
            if (this.#scale > MIN_ZOOM + 0.1) {
              this.#resetZoomState();
              this.#applyTransform();
            } else {
              this.#zoomToPoint(2.5, touch.clientX, touch.clientY, rect);
            }
          }
          this.#lastTapTime = 0;
          this.#lastTapPosition = null;
          return;
        }
      }

      this.#lastTapTime = now;
      this.#lastTapPosition = { clientX: touch.clientX, clientY: touch.clientY };
      this.#startDrag(touch.clientX, touch.clientY);
    }
  };

  /** @param {TouchEvent} event */
  #handleTouchMove = (event) => {
    event.preventDefault();
    if (event.touches.length === 2) {
      const t1 = event.touches[0];
      const t2 = event.touches[1];
      const dist = this.#getDistance(t1, t2);
      const newScale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, (dist / this.#initialPinchDistance) * this.#startScale));
      const midX = (t1.clientX + t2.clientX) / 2;
      const midY = (t1.clientY + t2.clientY) / 2;
      const rect = this.refs.dialog.querySelector('.lightbox__image-wrapper')?.getBoundingClientRect();
      if (rect) {
        this.#zoomToPoint(newScale, midX, midY, rect);
      }
      this.#isDragging = false;
    } else if (event.touches.length === 1 && this.#isDragging) {
      const touch = event.touches[0];
      this.#processDrag(touch.clientX, touch.clientY);
    }
  };

  /** @param {TouchEvent} event */
  #handleTouchEnd = (event) => {
    this.#isDragging = false;
    this.#hasDraggedBeyondThreshold = false;
  };

  // ─── KEYBOARD ─────────────────────

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.close();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        this.#navigate(-1);
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.#navigate(1);
        break;
      case '+':
      case '=':
        event.preventDefault();
        this.#zoomCenter(ZOOM_STEP);
        break;
      case '-':
        event.preventDefault();
        this.#zoomCenter(-ZOOM_STEP);
        break;
      case '0':
        event.preventDefault();
        this.#resetZoomState();
        this.#applyTransform();
        break;
    }
  };

  // ─── ZOOM HELPERS ─────────────────

  #zoomCenter(delta) {
    const rect = this.refs.dialog.querySelector('.lightbox__image-wrapper')?.getBoundingClientRect();
    if (!rect) return;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    this.#zoomToPoint(
      Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, this.#scale + delta)),
      cx, cy, rect
    );
  }

  #zoomToPoint(targetZoom, clientX, clientY, rect) {
    const oldScale = this.#scale;
    this.#scale = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, targetZoom));

    if (this.#scale <= MIN_ZOOM) {
      this.#translate = { x: 0, y: 0 };
    } else {
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      const distX = clientX - centerX;
      const distY = clientY - centerY;
      const scaleDelta = this.#scale / oldScale - 1;
      this.#translate.x -= (distX * scaleDelta) / this.#scale;
      this.#translate.y -= (distY * scaleDelta) / this.#scale;
    }

    this.#constrainTranslation();
    this.#applyTransform();
  }

  #constrainTranslation() {
    if (this.#scale <= MIN_ZOOM) {
      this.#translate = { x: 0, y: 0 };
      return;
    }

    const wrapper = this.refs.dialog.querySelector('.lightbox__image-wrapper');
    const img = this.refs.dialog.querySelector('.lightbox__image');
    if (!wrapper || !img) return;

    const wRect = wrapper.getBoundingClientRect();

    // Calculate the actual displayed image dimensions (object-fit: contain)
    let imgW, imgH;
    if (img.naturalWidth > 0 && img.naturalHeight > 0) {
      const imgAR = img.naturalWidth / img.naturalHeight;
      const wAR = wRect.width / wRect.height;
      if (imgAR > wAR) {
        imgW = wRect.width;
        imgH = imgW / imgAR;
      } else {
        imgH = wRect.height;
        imgW = imgH * imgAR;
      }
    } else {
      imgW = wRect.width;
      imgH = wRect.height;
    }

    const scaledW = imgW * this.#scale;
    const scaledH = imgH * this.#scale;
    const overflowX = Math.max(0, scaledW - wRect.width);
    const overflowY = Math.max(0, scaledH - wRect.height);
    const maxTX = overflowX / 2 / this.#scale;
    const maxTY = overflowY / 2 / this.#scale;

    this.#translate.x = Math.max(-maxTX, Math.min(maxTX, this.#translate.x));
    this.#translate.y = Math.max(-maxTY, Math.min(maxTY, this.#translate.y));
  }

  #applyTransform() {
    const container = this.refs.dialog.querySelector('.lightbox__image-container');
    if (!container) return;

    container.style.transform = `scale(${this.#scale}) translate(${this.#translate.x}px, ${this.#translate.y}px)`;

    // Update cursor
    const wrapper = this.refs.dialog.querySelector('.lightbox__image-wrapper');
    if (wrapper) {
      wrapper.style.cursor = this.#scale > MIN_ZOOM ? 'grab' : 'zoom-in';
    }

    // Update zoom level indicator
    const zoomLevel = this.refs.dialog.querySelector('.lightbox__zoom-level');
    if (zoomLevel) {
      const pct = Math.round(this.#scale * 100);
      zoomLevel.textContent = `${pct}%`;
      zoomLevel.classList.toggle('lightbox__zoom-level--visible', this.#scale > MIN_ZOOM);
    }

    // Update zoom control states
    const zoomInBtn = this.refs.dialog.querySelector('.lightbox__btn--zoom-in');
    const zoomOutBtn = this.refs.dialog.querySelector('.lightbox__btn--zoom-out');
    if (zoomInBtn) zoomInBtn.disabled = this.#scale >= MAX_ZOOM;
    if (zoomOutBtn) zoomOutBtn.disabled = this.#scale <= MIN_ZOOM;
  }

  // ─── DRAG HELPERS ─────────────────

  #startDrag(clientX, clientY) {
    this.#startPosition = { x: clientX, y: clientY };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
    this.#hasDraggedBeyondThreshold = false;

    const wrapper = this.refs.dialog.querySelector('.lightbox__image-wrapper');
    if (wrapper && this.#scale > MIN_ZOOM) {
      wrapper.style.cursor = 'grabbing';
    }
  }

  #processDrag(clientX, clientY) {
    const dx = clientX - this.#startPosition.x;
    const dy = clientY - this.#startPosition.y;
    const dist = Math.sqrt(dx * dx + dy * dy);

    if (!this.#hasDraggedBeyondThreshold && dist < DRAG_THRESHOLD) return;
    this.#hasDraggedBeyondThreshold = true;

    if (this.#scale > MIN_ZOOM) {
      // Pan the image
      this.#translate.x = this.#startTranslate.x + dx / this.#scale;
      this.#translate.y = this.#startTranslate.y + dy / this.#scale;
      this.#constrainTranslation();
      this.#requestApplyTransform();
    }
  }

  #requestApplyTransform() {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(() => {
        this.#animationFrame = null;
        this.#applyTransform();
      });
    }
  }

  #resetZoomState() {
    this.#scale = MIN_ZOOM;
    this.#translate = { x: 0, y: 0 };
    this.#isDragging = false;
    this.#hasDraggedBeyondThreshold = false;
    this.#lastTapTime = 0;
    this.#lastTapPosition = null;
  }

  // ─── COUNTER ───────────────────────

  #updateCounter() {
    const counter = this.refs.dialog.querySelector('.lightbox-counter span');
    if (counter) {
      counter.textContent = this.#activeIndex + 1;
    }
  }

  // ─── NAVIGATION ───────────────────

  #navigate(direction) {
    const { media } = this.refs;
    // Only navigate through image media
    const totalMedia = media.length;
    let newIndex = this.#activeIndex + direction;
    if (newIndex < 0) newIndex = totalMedia - 1;
    if (newIndex >= totalMedia) newIndex = 0;

    this.#activeIndex = newIndex;
    this.#resetZoomState();
    this.#updateLightboxImage();
    this.#updateThumbnailSelection();
    this.#loadCurrentHighRes();
    this.#updateCounter();
    this.dispatchEvent(new ZoomMediaSelectedEvent(this.#activeIndex));
  }

  // ─── THUMBNAILS ───────────────────

  #updateThumbnailSelection() {
    const { thumbnails } = this.refs;
    if (!thumbnails) return;

    const buttons = thumbnails.querySelectorAll('button');
    buttons.forEach((btn, i) => {
      btn.setAttribute('aria-selected', `${i === this.#activeIndex}`);
    });

    // Scroll active thumbnail into view
    const activeThumb = buttons[this.#activeIndex];
    if (activeThumb) {
      activeThumb.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index
   */
  async handleThumbnailClick(index) {
    if (index === this.#activeIndex) return;
    this.#activeIndex = index;
    this.#resetZoomState();
    this.#updateLightboxImage();
    this.#updateThumbnailSelection();
    this.#loadCurrentHighRes();
    this.#updateCounter();
    this.dispatchEvent(new ZoomMediaSelectedEvent(this.#activeIndex));
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index
   */
  async handleThumbnailPointerEnter(index) {
    // Preload high-res for this index
    const { media } = this.refs;
    if (media[index]) {
      this.loadHighResolutionImage(media[index]);
    }
  }

  // ─── HIGH-RES LOADING ─────────────

  #loadCurrentHighRes() {
    const { media } = this.refs;
    const currentMedia = media[this.#activeIndex];
    if (currentMedia) {
      this.loadHighResolutionImage(currentMedia);
    }
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);
    this.#highResImagesLoaded.add(highResolutionUrl);
  }

  // ─── CLOSE ────────────────────────

  async close() {
    const { dialog } = this.refs;

    // Stop any playing video
    const container = dialog.querySelector('.lightbox__image-container');
    if (container) this.#stopCurrentVideo(container);

    // Animate out
    dialog.classList.add('lightbox-closing');

    // Wait for animation
    await new Promise(resolve => {
      const handler = () => {
        dialog.removeEventListener('animationend', handler);
        resolve();
      };
      dialog.addEventListener('animationend', handler);
      // Fallback
      setTimeout(resolve, 400);
    });

    dialog.classList.remove('lightbox-closing');
    dialog.close();
    document.documentElement.removeAttribute('scroll-lock');

    this.#controller.abort();
    window.dispatchEvent(new DialogCloseEvent());
  }

  closeDialog() {
    this.close();
  }

  /**
   * Handles keydown - compatibility with theme framework
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  /**
   * Selects a thumbnail - compatibility with existing scroll handling
   * @param {number} index
   * @param {Object} options
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    this.handleThumbnailClick(index);
  }

  /**
   * Toggles full screen state
   */
  toggleFullscreen() {
    const dialog = this.refs.dialog;
    if (document.fullscreenElement) {
      if (document.exitFullscreen) document.exitFullscreen();
    } else {
      if (dialog.requestFullscreen) {
        dialog.requestFullscreen();
      } else if (dialog.webkitRequestFullscreen) {
        dialog.webkitRequestFullscreen();
      }
    }
  }

  // ─── UTILITY ──────────────────────

  #getDistance(p1, p2) {
    const x1 = p1.x ?? p1.clientX;
    const y1 = p1.y ?? p1.clientY;
    const x2 = p2.x ?? p2.clientX;
    const y2 = p2.y ?? p2.clientY;
    return Math.sqrt((x1 - x2) ** 2 + (y1 - y2) ** 2);
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}
